import React from 'react';
import { InsuranceTable } from '../../components/InsuranceTable/InsuranceTable';
import { ColumnHeader, InsuranceToDelete, InsuranceToUpsert } from '../../Insurance.meta';
import { InsuranceDTO, InsuranceType } from '../../api/useInsuranceQuery';
import { formatCurrency } from '../../../../CommonUtils/formatNumber';
import { Text } from '../../../../CommonComponents/OPL/Text/Text';
import { TableActions } from '../../components/TableActions/TableActions';
import { WorkflowComponentProps } from '../../../../Workflow/Workflow.meta';
import { WorkflowIdEnum } from '../../../../Workflow/api/useWorkflowDataQuery';
import { Icon } from '../../../../CommonComponents/OPL/Icon/Icon';
import { useIsPdf } from '../../../../FinancialPlanOPL/hooks/useIsPdf';

interface GetColumnHeadersParams {
  isEstatePlanningWorkflow: boolean;
}

const getColumnHeaders = ({ isEstatePlanningWorkflow }: GetColumnHeadersParams): ColumnHeader[] => {
  const premiumColumn: ColumnHeader = isEstatePlanningWorkflow
    ? { text: 'In Gr. Est.', align: 'center' }
    : { text: 'Premium', align: 'right' };

  return [
    { text: 'Name' },
    { text: 'Owner' },
    { text: 'Death Benefit', align: 'right' },
    { text: 'Policy Expiration' },
    { text: 'Cash Value', align: 'right' },
    premiumColumn,
  ];
};

interface Props {
  data: InsuranceDTO;
  setInsuranceToUpsert(insurance: InsuranceToUpsert): void;
  setInsuranceToDelete(insurance: InsuranceToDelete): void;
  workflowProps?: WorkflowComponentProps;
}

export const LifeInsurance = ({
  data,
  setInsuranceToUpsert,
  setInsuranceToDelete,
  workflowProps,
}: Props) => {
  const isEstatePlanningWorkflow = !!(
    workflowProps && workflowProps.workflowId === WorkflowIdEnum.EstatePlanning
  );

  const items = data.Items.Life || [];

  const isPrint = useIsPdf();

  return (
    <InsuranceTable
      title="Life Insurance Policies"
      isEmpty={items.length === 0}
      columnHeaders={getColumnHeaders({ isEstatePlanningWorkflow })}
      onAddInsurance={() => setInsuranceToUpsert({ InsuranceType: InsuranceType.Life })}
      workflowProps={workflowProps}
    >
      {items.map(insurance => (
        <tr key={insurance.AccountId}>
          <td>
            <Text fontWeight={500} textAlign="left" width="100%">
              {insurance.Description}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.AccountOwner}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.LifeInsurancePolicyAmount !== null
                ? formatCurrency(insurance.LifeInsurancePolicyAmount)
                : ''}
            </Text>
          </td>

          <td>
            <Text fontWeight={500} textAlign="left">
              {insurance.InsuranceExpirationYear}
            </Text>
          </td>

          <td>
            <Text variant="tabular" fontWeight={500} textAlign="right">
              {insurance.NonSpecificPositionsValue !== null
                ? formatCurrency(insurance.NonSpecificPositionsValue)
                : ''}
            </Text>
          </td>

          {isEstatePlanningWorkflow ? (
            <td>
              <Text textAlign="center">
                {insurance.IncludeInGrossEstate ? (
                  <Icon className="fas fa-check" color="success.500" />
                ) : (
                  <Icon className="fas fa-times-octagon" color="error.500" />
                )}
              </Text>
            </td>
          ) : (
            <td>
              <Text variant="tabular" fontWeight={500} textAlign="right">
                {insurance.Premium !== null ? formatCurrency(insurance.Premium) : ''}
              </Text>
            </td>
          )}

          {!isPrint && (
            <TableActions
              insuranceName={insurance.Description}
              onEdit={() => setInsuranceToUpsert(insurance)}
              onDelete={() => setInsuranceToDelete({ id: insurance.AccountId })}
            />
          )}
        </tr>
      ))}
    </InsuranceTable>
  );
};
